/**
 * BuyerMapView — Leaflet map of cash buyers (mirrors Discover.jsx's idiom).
 *
 * Pin colors by sale propensity:
 *   green  (Hot, >=80)   amber (Warm, >=60)   gray (Cold)
 * Distress overrides pin to RED so seller opportunities pop visually.
 * Millionaire owners get a small gem badge in the popup.
 *
 * Clicking a pin opens a popup with the buyer's key intel + jumps the parent
 * grid to that buyer card (handled via onPinClick callback so the existing
 * BuyerResultCard is the single source of truth for actions).
 */
import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Gem, Flame, ShieldAlert } from "lucide-react";

function pinIcon(color, ring) {
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
  <path d="M14 0C6.3 0 0 6.3 0 14c0 9.6 14 26 14 26s14-16.4 14-26C28 6.3 21.7 0 14 0z" fill="${color}"/>
  <circle cx="14" cy="14" r="5" fill="#fff"/>
  ${ring ? `<circle cx="14" cy="14" r="11" fill="none" stroke="${ring}" stroke-width="2.5"/>` : ""}
</svg>`;
  return L.divIcon({
    html: svg,
    className: "af-pin",
    iconSize: [28, 40],
    iconAnchor: [14, 40],
    popupAnchor: [0, -36],
  });
}

const PIN_HOT = pinIcon("#10B981");
const PIN_WARM = pinIcon("#F59E0B");
const PIN_COLD = pinIcon("#94A3B8");
const PIN_DISTRESS = pinIcon("#E11D48", "#FBBF24"); // red with gold ring

function pinFor(buyer) {
  if (buyer.has_distress) return PIN_DISTRESS;
  const score = buyer.sale_propensity_score ?? buyer.activity_score ?? 0;
  if (score >= 80) return PIN_HOT;
  if (score >= 60) return PIN_WARM;
  return PIN_COLD;
}

function MapRecenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center && Number.isFinite(center[0]) && Number.isFinite(center[1])) {
      map.flyTo(center, 11, { duration: 0.9 });
    }
  }, [center, map]);
  return null;
}

export default function BuyerMapView({ buyers, onPinClick }) {
  const positioned = useMemo(
    () => (buyers || []).filter(
      (b) => Number.isFinite(b.latitude) && Number.isFinite(b.longitude),
    ),
    [buyers],
  );

  const center = useMemo(() => {
    if (positioned.length === 0) return [39.5, -98.5]; // continental USA
    const lat = positioned.reduce((a, b) => a + b.latitude, 0) / positioned.length;
    const lng = positioned.reduce((a, b) => a + b.longitude, 0) / positioned.length;
    return [lat, lng];
  }, [positioned]);

  return (
    <div
      className="card p-0 overflow-hidden"
      style={{ height: "72vh" }}
      data-testid="buyer-map-wrap"
    >
      <MapContainer
        center={center}
        zoom={positioned.length ? 11 : 4}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapRecenter center={center} />
        {positioned.map((b) => (
          <Marker
            key={b.id}
            position={[b.latitude, b.longitude]}
            icon={pinFor(b)}
            eventHandlers={{ click: () => onPinClick?.(b) }}
          >
            <Popup>
              <div className="text-xs" data-testid={`buyer-popup-${b.id}`}>
                <div className="flex items-center gap-1 mb-0.5">
                  <span className="font-semibold">{b.name}</span>
                  {b.is_millionaire && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[9px] uppercase font-bold tracking-wider px-1 py-[1px] rounded bg-gradient-to-r from-amber-400 to-yellow-500 text-white"
                      title="Estimated net worth ≥ $1M"
                    >
                      <Gem size={8} /> M
                    </span>
                  )}
                  {b.has_distress && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[9px] uppercase font-bold tracking-wider px-1 py-[1px] rounded bg-rose-600 text-white"
                      title="Distress signals — potential seller opportunity"
                    >
                      <ShieldAlert size={8} /> Distress
                    </span>
                  )}
                </div>
                <div className="text-[var(--ink-3)] mb-1">
                  {b.primary_market} · {b.properties_owned} props
                </div>
                <div className="flex items-center gap-2 text-[var(--ink-2)]">
                  <span>
                    <Flame size={10} className="inline -mt-0.5 mr-0.5 text-emerald-600" />
                    {b.sale_propensity_score ?? b.activity_score}/99
                  </span>
                  {b.net_worth_label && <span>NW {b.net_worth_label}</span>}
                </div>
                <button
                  type="button"
                  onClick={() => onPinClick?.(b)}
                  className="mt-2 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-emerald-600 text-white"
                  data-testid={`buyer-popup-open-${b.id}`}
                >
                  Open card →
                </button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
